import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  abandonOngoingGame,
  getAlbums,
  getCurrentGame,
  getDailyProgress,
  getFirstRound,
  getGameConfig,
  setAllDone,
  setAnswer,
  setHint,
  setPupStarCardsCollected,
  startGame,
} from "../../../api/apiGame";
import {
  collectDailyBonus,
  collectPersonalPick,
  deleteUserPersonalCard,
  getUserPersonalCards,
} from "../../../api/userPersonalCard";
import DogCard from "../../dog-card/DogCard";
import Score from "../score/Score";
import { useNavigate, useLocation } from "react-router-dom";
import { getAllBreeds } from "../../../api/apiService";
import { getDogDestructivePhrases } from "../../../api/dogDestructivePhrases";
import AppContext from "../../../common/context/app-context";
import {
  GUEST_ID_API,
  GUEST_TOKEN_KEY,
  guestLoginApi,
  meApi,
  signInWithToken,
} from "../../../api/apiAuth";
import { Popup } from "../../../common/components/AuthModal";
import useQueryParams from "../../../common/hooks/useQueryParams";
import {
  useTrackGoogleAnalyticsEvent,
  useTrackGoogleAnalyticsEventPages,
} from "../../../common/hooks/useTrackGoogleAnalyticsEvent";
import { getAuth } from "firebase/auth";
import { mixpanelTrack } from "../../../utils/mixpanel-track";
import { getCourseItemData } from "../../../api/apiCourse";
import { getIsReactNativeWrapper, getIsV2AppVersion, getSessionId } from "../../../App";
import * as Sentry from "@sentry/react";

function tryOpenDeepLink(deepLink: string, fallbackUrl: string, timeout: number = 1500) {
  console.log("[tryOpenDeepLink] Starting deep link attempt", {
    deepLink,
    fallbackUrl,
    timeout,
    currentUrl: window.location.href,
    timestamp: new Date().toISOString(),
  });

  let didOpen = false;

  // Page unload detection
  function handlePageHide() {
    console.log("[tryOpenDeepLink] Page hide detected - app likely opened");
    didOpen = true;
  }
  window.addEventListener("pagehide", handlePageHide);
  console.log("[tryOpenDeepLink] Added pagehide listener");

  try {
    // Attempt to open the deep link
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = deepLink;
    document.body.appendChild(iframe);
    console.log("[tryOpenDeepLink] Created and appended iframe with deep link");
  } catch (error) {
    console.error("[tryOpenDeepLink] Error creating iframe:", error);
  }

  // After timeout, check if app didn't open
  const timeoutId = setTimeout(() => {
    console.log("[tryOpenDeepLink] Timeout reached", {
      didOpen,
      timestamp: new Date().toISOString(),
    });

    window.removeEventListener("pagehide", handlePageHide);
    console.log("[tryOpenDeepLink] Removed pagehide listener");

    if (!didOpen) {
      // Check if we're already on the fallback URL to prevent infinite loops
      const isFallbackUrl = window.location.href.includes(new URL(fallbackUrl).pathname);
      console.log("[tryOpenDeepLink] App did not open", {
        isFallbackUrl,
        currentUrl: window.location.href,
        fallbackUrl,
      });

      if (!isFallbackUrl) {
        console.log("[tryOpenDeepLink] Redirecting to fallback:", fallbackUrl);
        window.location.href = fallbackUrl;
      } else {
        console.log("[tryOpenDeepLink] Already on fallback URL, skipping redirect to prevent infinite loop");
      }
    } else {
      console.log("[tryOpenDeepLink] App opened, no fallback needed");
    }

    try {
      const iframe = document.querySelector(`iframe[src="${deepLink}"]`) as HTMLIFrameElement;
      if (iframe && iframe.parentNode) {
        document.body.removeChild(iframe);
        console.log("[tryOpenDeepLink] Removed iframe");
      }
    } catch (error) {
      console.error("[tryOpenDeepLink] Error removing iframe:", error);
    }
  }, timeout);

  console.log("[tryOpenDeepLink] Timeout scheduled with ID:", timeoutId);
}

export enum RoundType {
  TileGame = 1,
  FourPhoto,
  SinglePhoto,
  SingleVideo,
  FindTheLie,
}

type RoundEventData = { roundNumber: number; type: string };

interface GameProp {
  isCourse?: boolean;
  courseId?: string;
  url?: string;
  imageLoadEndHandler?: (img: string) => void;
  previewClass?: string;
}

const postMessageToParent = (data: { type: string; data: any }): void => {
  window.parent?.postMessage(data, "*");
};

const GAME_LOAD_TIMEOUT_MS = 15000;
const RN_POINTS_BY_TOPIC_STORAGE_KEY = "rnPointsByTopicAccumulated";
const RN_SCORE_STORAGE_KEY = "rnScoreAccumulated";
const RN_SESSION_SCORE_STORAGE_KEY = "rnSessionScoreAccumulated";

type PointsByTopic = Record<string, number>;

type StoredPointsByTopic = {
  dayKey: string;
  userId: number | null;
  sessionId?: string;
  pointsByTopic: PointsByTopic;
  lastGameId: number | null;
};

type StoredScore = {
  dayKey: string;
  userId: number | null;
  totalScore: number;
  lastGameId: number | null;
};

type StoredSessionScore = {
  dayKey: string;
  userId: number | null;
  sessionId: string;
  totalPoints: number;
  lastGameId: number | null;
};

const getErrorPayload = (error: any) => ({
  message: error?.message ?? String(error),
  status: error?.response?.status,
  errorCode: error?.response?.data?.errorCode,
  response: error?.response?.data,
});

const getDayKey = (): string => new Date().toISOString().slice(0, 10);

const normalizePointsByTopic = (value: unknown): PointsByTopic => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.entries(value as Record<string, unknown>).reduce<PointsByTopic>(
    (acc, [topicId, points]) => {
      const numericPoints = Number(points);
      if (Number.isFinite(numericPoints) && numericPoints !== 0) {
        acc[topicId] = numericPoints;
      }
      return acc;
    },
    {}
  );
};

const readStoredPointsByTopic = (): StoredPointsByTopic | null => {
  try {
    const raw = window.localStorage.getItem(RN_POINTS_BY_TOPIC_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredPointsByTopic;
    return {
      dayKey: parsed?.dayKey,
      userId:
        typeof parsed?.userId === "number" ? parsed.userId : null,
      sessionId:
        typeof parsed?.sessionId === "string" ? parsed.sessionId : undefined,
      pointsByTopic: normalizePointsByTopic(parsed?.pointsByTopic),
      lastGameId:
        typeof parsed?.lastGameId === "number" ? parsed.lastGameId : null,
    };
  } catch {
    return null;
  }
};

const writeStoredPointsByTopic = (value: StoredPointsByTopic): void => {
  try {
    window.localStorage.setItem(
      RN_POINTS_BY_TOPIC_STORAGE_KEY,
      JSON.stringify(value)
    );
  } catch {
    // Ignore storage failures; payload still falls back to current game points.
  }
};

const clearStoredPointsByTopic = (): void => {
  try {
    window.localStorage.removeItem(RN_POINTS_BY_TOPIC_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
};

const readStoredScore = (): StoredScore | null => {
  try {
    const raw = window.localStorage.getItem(RN_SCORE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredScore;
    return {
      dayKey: parsed?.dayKey,
      userId: typeof parsed?.userId === "number" ? parsed.userId : null,
      totalScore: Number.isFinite(Number(parsed?.totalScore))
        ? Number(parsed.totalScore)
        : 0,
      lastGameId:
        typeof parsed?.lastGameId === "number" ? parsed.lastGameId : null,
    };
  } catch {
    return null;
  }
};

const writeStoredScore = (value: StoredScore): void => {
  try {
    window.localStorage.setItem(RN_SCORE_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Ignore storage failures; payload still falls back to current game score.
  }
};

const clearStoredScore = (): void => {
  try {
    window.localStorage.removeItem(RN_SCORE_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
};

const readStoredSessionScore = (): StoredSessionScore | null => {
  try {
    const raw = window.localStorage.getItem(RN_SESSION_SCORE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSessionScore;
    return {
      dayKey: parsed?.dayKey,
      userId: typeof parsed?.userId === "number" ? parsed.userId : null,
      sessionId: typeof parsed?.sessionId === "string" ? parsed.sessionId : "",
      totalPoints: Number.isFinite(Number(parsed?.totalPoints))
        ? Number(parsed.totalPoints)
        : 0,
      lastGameId:
        typeof parsed?.lastGameId === "number" ? parsed.lastGameId : null,
    };
  } catch {
    return null;
  }
};

const writeStoredSessionScore = (value: StoredSessionScore): void => {
  try {
    window.localStorage.setItem(
      RN_SESSION_SCORE_STORAGE_KEY,
      JSON.stringify(value)
    );
  } catch {
    // Ignore storage failures; payload still falls back to current game score.
  }
};

const clearStoredSessionScore = (): void => {
  try {
    window.localStorage.removeItem(RN_SESSION_SCORE_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
};

const parseScoreValue = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return null;
  const cleaned = value.replace(/[^\d.-]/g, "");
  if (!cleaned) return null;
  const numericScore = Number(cleaned);
  return Number.isFinite(numericScore) ? numericScore : null;
};

const mergePointsByTopic = (
  base: PointsByTopic,
  extra: PointsByTopic
): PointsByTopic => {
  const merged: PointsByTopic = { ...base };
  Object.entries(extra).forEach(([topicId, points]) => {
    merged[topicId] = (merged[topicId] ?? 0) + points;
  });
  return merged;
};

const sumPointsByTopic = (pointsByTopic: PointsByTopic): number =>
  Object.values(pointsByTopic).reduce((sum, points) => sum + points, 0);

const postMessageToReactNative = (type: string, data: any): void => {
  const webView = (window as any).ReactNativeWebView;
  if (!webView) return;
  webView.postMessage(JSON.stringify({ type, data }));
};

const Game: React.FC<GameProp> = ({
  isCourse = false,
  courseId,
  url = "",
  imageLoadEndHandler,
  previewClass = "game-iframe",
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showMode, topicId: topicIdParam } = useQueryParams() as {
    showMode?: string;
    topicId?: string;
  };
  const authToken = useMemo(() => {
    return new URLSearchParams(location.search).get("token");
  }, [location.search]);
  const topicIdForGame = useMemo(() => {
    if (topicIdParam == null || topicIdParam === "") {
      return undefined;
    }
    const n = Number(topicIdParam);
    return Number.isFinite(n) ? n : undefined;
  }, [topicIdParam]);
  const logGoogleEvent = useTrackGoogleAnalyticsEvent();

  // Use isNewSeq from context
  const { isNewSeq, setIsNewSeq } = useContext(AppContext);
  const isNewSeqRef = useRef(isNewSeq);

  // Ref to track if we've already attempted to open deep link
  const deepLinkAttemptedRef = useRef(false);

  // Removed sessionStorage and navigation state logic for isNewSeq
  // (was previously handled in a useEffect here)

  useEffect(() => {
    isNewSeqRef.current = isNewSeq;
  }, [isNewSeq]);
  console.log("=============>", isNewSeq);
  const gameFrameRef = useRef<HTMLIFrameElement | null>(null);
  const gameConfig: any = useRef();
  const { setShowAuthPopup, setIsGameMode, me, setMe, setAlbums } =
    useContext(AppContext);
  let lastRound: RoundEventData | null = null;

  const [hasEvent, setHasEvent] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openScore, setOpenScore] = useState<boolean>(false);
  const [finishData, setFinishData] = useState<any>(null);
  const [userPersonalCardId, setUserPersonalCardId] = useState<
    number | undefined
  >();
  const [imageData, setImageData] = useState<string>("");
  const [tokenLoginPending, setTokenLoginPending] = useState<boolean>(
    () => !!new URLSearchParams(window.location.search).get("token")
  );
  const [tokenLoginError, setTokenLoginError] = useState<string | null>(null);
  const [tokenLoginRetryKey, setTokenLoginRetryKey] = useState<number>(0);
  const loadEndReceivedRef = useRef<boolean>(false);
  const monitoredIframeUrlRef = useRef<string>("");
  const shouldClearAccumulatedAfterScoreSendRef = useRef<boolean>(false);
  const postMessage = useCallback(
    (data: { type: string; data: any }): void => {
      if (gameFrameRef?.current) {
        gameFrameRef.current.contentWindow?.postMessage(data, "*");
      }
    },
    [gameFrameRef?.current]
  );

  useEffect(() => {
    if (!getIsReactNativeWrapper() || !getIsV2AppVersion()) return;
    if (!getSessionId()) {
      // Without a host-provided session id, keep the legacy clean-start behavior.
      clearStoredPointsByTopic();
      clearStoredScore();
      clearStoredSessionScore();
    }
    shouldClearAccumulatedAfterScoreSendRef.current = false;
  }, []);

  // FIXME: Deep link logic disabled for now since it causes issues in browsers on desktop
  // useEffect(() => {
  //   // Attempt to open deep link to mobile app - only once per component mount
  //   console.log("[Game] Deep link effect running", {
  //     attemptedBefore: deepLinkAttemptedRef.current,
  //     isReactNativeWrapper: getIsReactNativeWrapper(),
  //   });

  //   if (!deepLinkAttemptedRef.current && !getIsReactNativeWrapper()) {
  //     console.log("[Game] Initiating deep link attempt");
  //     deepLinkAttemptedRef.current = true;
  //     tryOpenDeepLink(
  //       "flippedondogs://game/123",
  //       "https://flippedondogs.com/game"
  //     );
  //   } else {
  //     console.log("[Game] Skipping deep link - either already attempted or is React Native wrapper");
  //   }

  //   return () => {
  //     console.log("[Game] Component unmounting");
  //   };
  // }, []);

  useEffect(() => {
    const timeoutId = setTimeout(
      () => { 
        useTrackGoogleAnalyticsEventPages("/game", "game page")
      },
      3000
    );
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);
  useEffect(() => {
    setOpenScore(() => false);
    setImageData(() => "");
    postMessage({
      type: "newCourse",
      data: { courseId },
    });
  }, [courseId]);

  useEffect(() => {
    if (!authToken) {
      setTokenLoginError(null);
      setTokenLoginPending(false);
      return;
    }

    let cancelled = false;
    const auth = getAuth();

    const loginWithToken = async () => {
      setTokenLoginPending(true);
      setTokenLoginError(null);
      try {
        await signInWithToken(auth, authToken);
        const user = await meApi();
        setMe(user);
        localStorage.removeItem(GUEST_TOKEN_KEY);
        localStorage.removeItem(GUEST_ID_API);
      } catch (error) {
        const payload = getErrorPayload(error);
        console.error("Game token login failed:", payload);
        postMessageToReactNative("GameTokenLoginFailed", payload);
        if (!cancelled) {
          setTokenLoginError(
            payload.message || "Unable to sign in with the provided token."
          );
        }
      } finally {
        if (!cancelled) {
          setTokenLoginPending(false);
        }
      }
    };

    loginWithToken();

    return () => {
      cancelled = true;
    };
  }, [authToken, setMe, tokenLoginRetryKey]);

  useEffect(() => {
    if (tokenLoginPending || tokenLoginError) return;

    const initGuest = async () => {
      if (!localStorage.getItem(GUEST_TOKEN_KEY)) {
        try {
          const guestToken = await guestLoginApi();
          localStorage.setItem(GUEST_TOKEN_KEY, guestToken[GUEST_TOKEN_KEY]);
          console.log("Guest token initialized successfully");
        } catch (error) {
          console.log(`Failed to initialize guest token: ${error}`);
        }
      }
    };
    if (url !== "?openScrapBook" && url !== "?firstRoundImage") {
      getAuth()
        .authStateReady()
        .then(() => initGuest())
        .catch((error) => alert(`Auth state ready error: ${error}`));
    }
  }, [url, tokenLoginPending, tokenLoginError]);

  useEffect(() => {
    if (!me || !hasEvent) return;
    setHasEvent(false);
    getAlbums().then((albums) => {
      postMessage({
        type: "getAlbums",
        data: { ...albums, userId: me.id },
      });
    });
  }, [me, hasEvent]);

  useEffect(() => {
    const handleMessage = async ({ data }: any) => {
      const user = me;
      if (data.type == null) {
        return;
      }
      const currentData = data?.data ?? {};

      switch (data.type) {
        case "setItemToLocalStorage": {
          localStorage.setItem(currentData.key, currentData.value);
          break;
        }
        case "setEventsToMixpanel": {
          mixpanelTrack(currentData.eventName, { data: currentData.data });
          break;
        }
        case "getItemFromLocalStorage": {
          postMessage({
            type: "getItemFromLocalStorage",
            data: localStorage.getItem(currentData.key),
          });
          break;
        }
        case "gameSetup": {
          postMessage({
            type: "gameSetup",
            data: currentData,
          });
          if (showMode) {
            gameConfig.current = currentData;
          }
          break;
        }
        // case "loadEnd": {
        //   if (showMode) {
        //     postMessageToParent({
        //       type: "loadEnd",
        //       data: {},
        //     });
        //     return;
        //   }
        //   let type = "loadEnd";
        //   if (isCourse) {
        //     try {
        //       gameConfig.current = await getCourseItemData(courseId || '',
        //         user
        //           ? {}
        //           : {
        //             baseURL: process.env.REACT_APP_SERVER_API?.replace(
        //               "client",
        //               "guest",
        //             )
        //           }
        //       );
        //     } catch (e: any) {
        //       if (e?.response?.data?.errorCode === 'NO_UID') {
        //         const guestToken = await guestLoginApi();
        //         localStorage.setItem(GUEST_TOKEN_KEY, guestToken[GUEST_TOKEN_KEY]);
        //         try {
        //           gameConfig.current = await getCourseItemData(courseId || '',
        //             user
        //               ? {}
        //               : {
        //                 baseURL: process.env.REACT_APP_SERVER_API?.replace(
        //                   "client",
        //                   "guest",
        //                 )
        //               }
        //           );
        //         }catch (e: any) {
        //           console.error(e);
        //         }
        //         type = "ongoingConfig";
        //       }
        //     }
        //     type = "ongoingConfig";
        //   } else {
        //     try {
        //       if (isNewSeq) {
        //         gameConfig.current = await getGameConfig(
        //           user
        //             ? {}
        //             : {
        //                 baseURL: process.env.REACT_APP_SERVER_API?.replace(
        //                   "client",
        //                   "guest",
        //                 ),
        //               },
        //           isNewSeqRef.current
        //         );
        //         type = "gameConfig";
        //         setIsNewSeq(false);
        //       } else {
        //         gameConfig.current = await getCurrentGame(
        //           user
        //             ? {}
        //             : {
        //                 baseURL: process.env.REACT_APP_SERVER_API?.replace(
        //                   "client",
        //                   "guest",
        //                 ),
        //               },
        //         );
        //         type = "ongoingConfig";
        //       }
        //     } catch (e: any) {
        //       if (e?.response?.data?.errorCode === 'NO_UID') {
        //         const guestToken = await guestLoginApi();
        //         localStorage.setItem(GUEST_TOKEN_KEY, guestToken[GUEST_TOKEN_KEY]);
        //         try {
        //           if (isNewSeq) {
        //             gameConfig.current = await getGameConfig(
        //               user
        //                 ? {}
        //                 : {
        //                     baseURL: process.env.REACT_APP_SERVER_API?.replace(
        //                       'client',
        //                       'guest',
        //                     ),
        //                   },
        //               isNewSeqRef.current
        //             );
        //             type = 'gameConfig';
        //             setIsNewSeq(false);
        //           } else {
        //             gameConfig.current = await getCurrentGame(
        //               user
        //                 ? {}
        //                 : {
        //                     baseURL: process.env.REACT_APP_SERVER_API?.replace(
        //                       'client',
        //                       'guest',
        //                     ),
        //                   },
        //             );
        //             type = 'ongoingConfig';
        //           }
        //         } catch (e) {
        //           try {
        //             gameConfig.current = await getGameConfig(
        //               user
        //                 ? {}
        //                 : {
        //                     baseURL: process.env.REACT_APP_SERVER_API?.replace(
        //                       'client',
        //                       'guest',
        //                     ),
        //                   },
        //              isNewSeqRef.current
        //             );
        //             type = 'gameConfig';
        //           } catch (e) {
        //             postMessage({
        //               type: 'gameConfigError',
        //               data: (e as any)?.response?.data,
        //             });
        //           }
        //         }
        //       } else {
        //         try {
        //           gameConfig.current = await getGameConfig(
        //             user
        //               ? {}
        //               : {
        //                   baseURL: process.env.REACT_APP_SERVER_API?.replace(
        //                     'client',
        //                     'guest',
        //                   ),
        //                 },
        //            isNewSeqRef.current
        //           );
        //           type = 'gameConfig';
        //         } catch (e) {
        //           postMessage({
        //             type: 'gameConfigError',
        //             data: (e as any)?.response?.data,
        //           });
        //         }
        //       }
        //     }
        //   }
        //   postMessage({
        //     type,
        //     data: {...gameConfig, isGuest: !user, userId: user?.id},
        //   });
        //   break;
        // }

        case "loadEnd": {
          loadEndReceivedRef.current = true;
          if (showMode) {
            postMessageToParent({
              type: "loadEnd",
              data: {},
            });
            return;
          }
          let type = "loadEnd";
          if (isCourse) {
            try {
              gameConfig.current = await getCourseItemData(
                courseId || "",
                user
                  ? {}
                  : {
                      baseURL: process.env.REACT_APP_SERVER_API?.replace(
                        "client",
                        "guest"
                      ),
                    }
              );
            } catch (e: any) {
              if (e?.response?.data?.errorCode === "NO_UID") {
                const guestToken = await guestLoginApi();
                localStorage.setItem(
                  GUEST_TOKEN_KEY,
                  guestToken[GUEST_TOKEN_KEY]
                );
                try {
                  gameConfig.current = await getCourseItemData(
                    courseId || "",
                    user
                      ? {}
                      : {
                          baseURL: process.env.REACT_APP_SERVER_API?.replace(
                            "client",
                            "guest"
                          ),
                        }
                  );
                } catch (e: any) {
                  console.error(e);
                }
                type = "ongoingConfig";
              }
            }
            type = "ongoingConfig";
          } else {
            try {
              gameConfig.current = await getCurrentGame(
                user
                  ? {}
                  : {
                      baseURL: process.env.REACT_APP_SERVER_API?.replace(
                        "client",
                        "guest"
                      ),
                    },
                topicIdForGame
              );
              type = "ongoingConfig";
            } catch (e: any) {
              if (e?.response?.data?.errorCode === "NO_UID") {
                const guestToken = await guestLoginApi();
                localStorage.setItem(
                  GUEST_TOKEN_KEY,
                  guestToken[GUEST_TOKEN_KEY]
                );
                try {
                  gameConfig.current = await getCurrentGame(
                    user
                      ? {}
                      : {
                          baseURL: process.env.REACT_APP_SERVER_API?.replace(
                            "client",
                            "guest"
                          ),
                        },
                    topicIdForGame
                  );
                  type = "ongoingConfig";
                } catch (e) {
                  try {
                    if (isNewSeq) {
                      gameConfig.current = await getGameConfig(
                        user
                          ? {}
                          : {
                              baseURL:
                                process.env.REACT_APP_SERVER_API?.replace(
                                  "client",
                                  "guest"
                                ),
                            },
                        isNewSeqRef.current,
                        topicIdForGame
                      );
                      setIsNewSeq(false);
                      type = "gameConfig";
                    } else {
                      gameConfig.current = await getGameConfig(
                        user
                          ? {}
                        : {
                              baseURL:
                                process.env.REACT_APP_SERVER_API?.replace(
                                  "client",
                                  "guest"
                                ),
                            },
                        undefined,
                        topicIdForGame
                      );
                    }

                    type = "gameConfig";
                  } catch (e) {
                    postMessage({
                      type: "gameConfigError",
                      data: (e as any)?.response?.data,
                    });
                  }
                }
              } else {
                try {
                  if (isNewSeq) {
                    gameConfig.current = await getGameConfig(
                      user
                        ? {}
                        : {
                            baseURL: process.env.REACT_APP_SERVER_API?.replace(
                              "client",
                              "guest"
                            ),
                          },
                      isNewSeqRef.current,
                      topicIdForGame
                    );
                    setIsNewSeq(false);
                    type = "gameConfig";
                  } else {
                    gameConfig.current = await getGameConfig(
                      user
                        ? {}
                        : {
                            baseURL: process.env.REACT_APP_SERVER_API?.replace(
                              "client",
                              "guest"
                            ),
                          },
                      undefined,
                      topicIdForGame
                    );
                    type = "gameConfig";
                  }
                } catch (e) {
                  postMessage({
                    type: "gameConfigError",
                    data: (e as any)?.response?.data,
                  });
                }
              }
            }
          }
          postMessage({
            type,
            data: { ...gameConfig, isGuest: !user, userId: user?.id },
          });
          break;
        }
        case "openTopics": {
          navigate("/topics");
          break;
        }
        case "submitDog": {
          navigate("/submit-dog-step-1");
          break;
        }
        case "getFirstRound": {
          const firstRound = await getFirstRound();
          if (showMode) {
            postMessageToParent({
              type: "getFirstRound",
              data: {},
            });
            return;
          }
          postMessage({
            type: "getFirstRound",
            data: firstRound,
          });
          break;
        }
        case "daily-progress": {
          const dailyProgress = await getDailyProgress();
          postMessage({
            type: "daily-progress",
            data: dailyProgress,
          });
          break;
        }
        case "all-done": {
          const response = await setAllDone();
          // Delay reset until ScoreData is actually delivered to RN.
          shouldClearAccumulatedAfterScoreSendRef.current = true;
          postMessage({
            type: "all-done",
            data: response,
          });
          break;
        }
        case "firstRoundForShow": {
          postMessage({
            type: "getFirstRound",
            data: currentData,
          });
          break;
        }
        case "imageReady": {
          setImageData(currentData);
          if (imageLoadEndHandler) {
            imageLoadEndHandler(currentData);
          }
          break;
        }
        case "collectUserPupStar": {
          if (me) {
            console.log("collectUserPupStar game event");
            const albums = await getAlbums();
            setAlbums(albums);

            await setPupStarCardsCollected();
          }
          break;
        }
        case "collectDailyBonus": {
          await collectDailyBonus(currentData.id);
          if (me) {
            const albums = await getAlbums();
            setAlbums(albums);
          }
          postMessage({
            type: "DailyBonusCollected",
            data: {},
          });
          break;
        }
        case "Login":
          if (!me) {
            setShowAuthPopup(() => ({
              authStep: Popup.Login,
              options: { postLoggedInEventToGame: true },
            }));
          }
          break;
        case "loggedInEventToGame":
          postMessage({ type: "login", data: true });
          break;
        case "startGame":
          const gameInit = await startGame(
            gameConfig.current.id,
            user
              ? {}
              : {
                  baseURL: process.env.REACT_APP_SERVER_API?.replace(
                    "client",
                    "guest"
                  ),
                }
          );
          lastRound = {
            roundNumber: gameInit?.rounds?.roundsCount,
            type: RoundType[gameInit.rounds?.roundTypeId],
          };
          postMessage({
            type: "startData",
            data: gameInit,
          });
          break;
        case "getUserPersonalPickCards":
          const userPersonalCards = await getUserPersonalCards();
          postMessage({
            type: "getUserPersonalPickCards",
            data: userPersonalCards,
          });
          break;
        case "editUserPersonalPickCard": {
          const id = currentData.id;
          setUserPersonalCardId(id);
          setOpenModal(true);
          postMessage({
            type: "editUserPersonalPickCard",
            data: { id },
          });
          break;
        }
        case "createUserPersonalPickCard":
          setOpenModal(true);
          setUserPersonalCardId(undefined);
          postMessage({
            type: "createUserPersonalPickCard",
            data: {},
          });
          break;
        case "collectUserPersonalPickCard":
          const id = currentData.id;
          const res = await collectPersonalPick(id);
          postMessage({
            type: "collectUserPersonalPickCard",
            data: { res },
          });
          break;
        case "deleteUserPersonalPickCard": {
          const id = currentData.id;
          const res = await deleteUserPersonalCard(id);
          postMessage({
            type: "deleteUserPersonalPickCard",
            data: res,
          });
          break;
        }
        case "closeUserPersonalPickCard":
          setOpenModal(false);
          postMessage({
            type: "closeUserPersonalPickCard",
            data: {},
          });
          break;
        case "answer":
          const answerData = await setAnswer(
            gameConfig.current.id,
            currentData.answerId,
            currentData.secondAnswer,
            user
              ? {}
              : {
                  baseURL: process.env.REACT_APP_SERVER_API?.replace(
                    "client",
                    "guest"
                  ),
                }
          );
          postMessage({
            type: "answer",
            data: answerData,
          });
          break;
        case "hint":
          const hintData = await setHint(
            gameConfig.current.id,
            user
              ? {}
              : {
                  baseURL: process.env.REACT_APP_SERVER_API?.replace(
                    "client",
                    "guest"
                  ),
                }
          );
          postMessage({
            type: "hint",
            data: hintData,
          });
          break;
        case "getAlbums":
          setHasEvent(true);
          break;
        case "gameFinish":
          !showMode &&
            logGoogleEvent("Game", `Completed-gameId-${currentData.gameId}`);
          setFinishData(() => ({ ...currentData, showMode }));
          setOpenScore(() => true);
          postMessage({
            type: "imageReady",
            data: {},
          });
          break;
        case "exit":
          navigate("/");
          break;
        case "howToPlay":
          navigate("/how-to-play");
          break;
        case "reload":
          postMessage({ type: "reload", data: {} });
          break;
        case "getAllBreeds":
          const breeds = await getAllBreeds();
          postMessage({
            type: "getAllBreeds",
            data: breeds,
          });
          break;
        case "getDogDestructivePhrases":
          const phrases = await getDogDestructivePhrases();
          postMessage({
            type: "getDogDestructivePhrases",
            data: phrases,
          });
          break;
        case "personalPickCardPage":
          window.scrollTo(0, 0);
          break;
        case "CloseGameCalled": {
          if (
            getIsReactNativeWrapper() &&
            getIsV2AppVersion() &&
            (window as any).ReactNativeWebView
          ) {
            (window as any).ReactNativeWebView.postMessage(
              JSON.stringify({ type: "CloseGameCalled" })
            );
          }
          break;
        }
        default: {
          console.error("Unknown message key: ", data.type);
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      if (url !== "?openScrapBook" && url !== "?firstRoundImage" && !showMode) {
        if (!finishData) {
          logGoogleEvent(
            "Game",
            `Abandoned-Round Number-${lastRound?.roundNumber}-Round Type-${lastRound?.type}`
          );
        } else {
          logGoogleEvent("Game", `Completed`);
        }
      }
    };
  }, [gameFrameRef.current, me, courseId, topicIdForGame]);

  useEffect(() => {
    if (url === "?firstRoundImage") return;
    setIsGameMode(true);
    let root = document.getElementById("root");
    let gameHolderShow = document.getElementById("game-iframe-holder");
    root?.classList.add("game");
    if (showMode && gameHolderShow) {
      gameHolderShow.classList.remove("game-holder");
      gameHolderShow.classList.add("game-holder-show");
    }

    return () => {
      setIsGameMode(false);
      root?.classList.remove("game");
      root = null;
      gameHolderShow = null;
    };
  }, [url, setIsGameMode, showMode]);

  // Extract and send score data to React Native
  useEffect(() => {
    if (!getIsReactNativeWrapper()) return;

    function extractAndSend() {
      const titleEl = document.querySelector(
        ".fs-h-5.lh-h-5.xs-fs-h-7.xs-lh-h-7.fw-700.c-primary.mb-2"
      );
      const imageContainer = document.querySelector(".flex.flex-col.w-100");

      if (titleEl && titleEl.textContent?.includes("Today's Score")) {
        const parts = titleEl.textContent.split("Today's Score");
        const score = parts.length > 1 ? parts[1].trim() : null;

        let imageBase64 = null;
        if (imageContainer) {
          const imgEl = imageContainer.querySelector("img");
          if (
            imgEl &&
            (imgEl as HTMLImageElement).src.startsWith("data:image")
          ) {
            imageBase64 = (imgEl as HTMLImageElement).src;
          }
        }

        if (score && imageBase64 && (window as any).ReactNativeWebView) {
          const activeUserId = typeof me?.id === "number" ? me.id : null;
          const dayKey = getDayKey();
          const sessionId = getSessionId();
          const currentGameId =
            typeof finishData?.gameId === "number" ? finishData.gameId : null;
          const currentScore =
            parseScoreValue(finishData?.score) ?? parseScoreValue(score);
          const storedScore = readStoredScore();
          const canReuseStoredScore =
            storedScore?.dayKey === dayKey && storedScore?.userId === activeUserId;
          const baseTotalScore = canReuseStoredScore ? storedScore?.totalScore ?? 0 : 0;
          const shouldAccumulateScore =
            currentScore !== null &&
            (!canReuseStoredScore || storedScore?.lastGameId !== currentGameId);
          const totalScore = shouldAccumulateScore
            ? baseTotalScore + currentScore
            : baseTotalScore;
          const scoreToSend =
            currentScore !== null ? String(totalScore) : score;

          if (currentScore !== null) {
            writeStoredScore({
              dayKey,
              userId: activeUserId,
              totalScore,
              lastGameId: currentGameId,
            });
          }

          const payload: {
            type: string;
            score: string;
            image: string;
            pointsByTopic?: Record<string, number>;
            totalPoints?: number;
          } = {
            type: "ScoreData",
            score: scoreToSend,
            image: imageBase64,
          };
          if (getIsV2AppVersion()) {
            const currentGamePoints = normalizePointsByTopic(
              finishData?.pointsByTopicId
            );
            if (sessionId) {
              const storedSessionScore = readStoredSessionScore();
              const canReuseStoredSessionScore =
                storedSessionScore?.dayKey === dayKey &&
                storedSessionScore?.userId === activeUserId &&
                storedSessionScore?.sessionId === sessionId;
              const baseSessionScore = canReuseStoredSessionScore
                ? storedSessionScore?.totalPoints ?? 0
                : 0;
              const shouldAccumulateSessionScore =
                !canReuseStoredSessionScore ||
                storedSessionScore?.lastGameId !== currentGameId;
              const currentSessionPoints = sumPointsByTopic(currentGamePoints);
              const totalPoints = shouldAccumulateSessionScore
                ? baseSessionScore + currentSessionPoints
                : baseSessionScore;

              payload.totalPoints = totalPoints;
              writeStoredSessionScore({
                dayKey,
                userId: activeUserId,
                sessionId,
                totalPoints,
                lastGameId: currentGameId,
              });
            }

            const stored = readStoredPointsByTopic();
            const canReuseStored =
              stored?.dayKey === dayKey &&
              stored?.userId === activeUserId &&
              (sessionId
                ? stored?.sessionId === sessionId
                : stored?.sessionId == null);
            const basePoints = canReuseStored ? stored?.pointsByTopic ?? {} : {};
            const shouldAccumulatePoints =
              !canReuseStored || stored?.lastGameId !== currentGameId;
            const mergedPoints = shouldAccumulatePoints
              ? mergePointsByTopic(basePoints, currentGamePoints)
              : basePoints;

            payload.pointsByTopic = mergedPoints;
            writeStoredPointsByTopic({
              dayKey,
              userId: activeUserId,
              sessionId,
              pointsByTopic: mergedPoints,
              lastGameId: currentGameId,
            });
          }
          (window as any).ReactNativeWebView.postMessage(
            JSON.stringify(payload)
          );
          if (shouldClearAccumulatedAfterScoreSendRef.current) {
            clearStoredScore();
            if (!sessionId) {
              clearStoredPointsByTopic();
              clearStoredSessionScore();
            }
            shouldClearAccumulatedAfterScoreSendRef.current = false;
          }
          observer.disconnect();
        }
      }
    }

    const observer = new MutationObserver(() => {
      extractAndSend();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    extractAndSend();

    return () => observer.disconnect();
  }, [openScore, finishData, me?.id]);

  const gameIframeUrl = useMemo(() => {
    const rootUrl = process.env.REACT_APP_GAME_URL + url;
    const sp = new URLSearchParams();
    sp.set("isReactNativeWrapper", String(!!getIsReactNativeWrapper()));
    if (getIsV2AppVersion()) {
      sp.set("isV2AppVersion", "true");
      sp.set("isAppV2Version", "true");
    }
    const sessionId = getSessionId();
    if (sessionId) {
      sp.set("sessionId", sessionId);
    }
    if (topicIdForGame != null) {
      sp.set("topicId", String(topicIdForGame));
    }
    const q = sp.toString();
    return rootUrl.includes("?") ? `${rootUrl}&${q}` : `${rootUrl}?${q}`;
  }, [url, topicIdForGame, location.search]);

  useEffect(() => {
    if (
      showMode ||
      tokenLoginPending ||
      tokenLoginError ||
      imageData
    ) {
      return;
    }

    if (monitoredIframeUrlRef.current !== gameIframeUrl) {
      monitoredIframeUrlRef.current = gameIframeUrl;
      loadEndReceivedRef.current = false;
    }
    const timeoutId = window.setTimeout(() => {
      const hasIframe = !!gameFrameRef.current;
      if (hasIframe && loadEndReceivedRef.current) {
        return;
      }

      const payload = {
        hasIframe,
        loadEndReceived: loadEndReceivedRef.current,
        authTokenPresent: !!authToken,
        topicId: topicIdForGame,
        isReactNativeWrapper: getIsReactNativeWrapper(),
        isV2AppVersion: getIsV2AppVersion(),
        url,
        gameIframeUrl,
      };
      console.error("GameLoadTimeout", payload);
      Sentry.captureMessage("GameLoadTimeout", {
        level: "error",
        extra: payload,
      });
      postMessageToReactNative("GameLoadTimeout", payload);
    }, GAME_LOAD_TIMEOUT_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    authToken,
    gameIframeUrl,
    imageData,
    me,
    showMode,
    tokenLoginError,
    tokenLoginPending,
    topicIdForGame,
    url,
  ]);

  // React Native → web: close game WebView, then acknowledge (v2 + RN only).
  useEffect(() => {
    if (!getIsReactNativeWrapper() || !getIsV2AppVersion()) return;

    const onHostMessage = (event: MessageEvent) => {
      if (gameFrameRef.current?.contentWindow === event.source) {
        return;
      }
      let msg: any = event.data;
      if (typeof event.data === "string") {
        try {
          msg = JSON.parse(event.data);
        } catch {
          return;
        }
      }
      if (msg?.type !== "CloseGameWebView") return;

      void (async () => {
        try {
          await abandonOngoingGame(
            me
              ? {}
              : {
                  baseURL: process.env.REACT_APP_SERVER_API?.replace(
                    "client",
                    "guest"
                  ),
                },
            topicIdForGame
          );
        } catch (e) {
          console.error("abandonOngoingGame failed", e);
        }

        postMessage({ type: "CloseGameWebView", data: {} });
        const w = window as any;
        if (w.ReactNativeWebView) {
          w.ReactNativeWebView.postMessage(
            JSON.stringify({ type: "CloseGameApproved" })
          );
        }
        navigate("/");
      })();
    };

    window.addEventListener("message", onHostMessage);
    document.addEventListener("message", onHostMessage as EventListener);
    return () => {
      window.removeEventListener("message", onHostMessage);
      document.removeEventListener("message", onHostMessage as EventListener);
    };
  }, [navigate, postMessage, me, topicIdForGame]);

  return (
    <>
      {openModal && (
        <DogCard
          id={userPersonalCardId}
          onClose={(data) => {
            setOpenModal(!openModal);
            postMessage({ type: "closeUserPersonalPickCard", data }); // TODO
          }}
        ></DogCard>
      )}
      <div
        id="game-iframe-holder"
        className={"game-holder" + (openScore ? " invisible" : "")}
        style={getIsReactNativeWrapper() ? { minHeight: "100vh" } : undefined}
      >
        {tokenLoginError && (
          <div className="flex flex-col justify-center align-center text-center w-100 h-100 px-6 row-gap-6">
            <span className="fs-xl lh-xl fw-700 c-text-black">
              We couldn't start the game
            </span>
            <span className="fs-md lh-md c-text-4">
              Your app session could not be verified. Please try again.
            </span>
            <button
              type="button"
              className="btn btn--primary xs-btn--lg"
              onClick={() => {
                setTokenLoginError(null);
                setTokenLoginRetryKey((value) => value + 1);
              }}
            >
              Try again
            </button>
          </div>
        )}
        {!tokenLoginError && !tokenLoginPending && !imageData && me !== undefined && (
          <iframe
            className={previewClass}
            src={gameIframeUrl}
            title="game"
            ref={gameFrameRef as any}
          ></iframe>
        )}
      </div>
      {openScore && (
        <Score
          data={finishData}
          roundCount={gameConfig.current.rounds.length}
          userId={me?.id}
          imageData={imageData}
        />
      )}
    </>
  );
};

export default Game;
